import {
  DEFAULT_ONBOARDING_DRAFT,
  draftToProfile,
  normalizeLanguageEntries,
  normalizeOnboardingDraft,
} from "../onboarding/onboardingDraft";
import { clearOnboardingResumePath } from "../onboarding/onboardingSession";
import { normalizeUsername } from "../../shared/utils/usernameValidation";
import { loadItem, removeItem, saveItem, storageKeys } from "../../shared/storage/index";
import {
  fetchMyProfile,
  upsertMyProfile,
  uploadProfileImage,
  replaceMyPhotos,
} from "../../api/profileApi";
import { isPreviewAuthEnabled } from "../auth/previewAuth";
import { normalizeProfileTypes } from "../../shared/data/profileTaxonomy";
import { getProfileLocationLabel } from "../../shared/utils/profileLocation";

export const PROFILE_PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='1000'%3E%3Crect width='800' height='1000' fill='%23f5f3ef'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23c4a574' font-family='Arial' font-size='28'%3EAdd photo%3C/text%3E%3C/svg%3E";

export function filterValidPhotos(photos) {
  if (!Array.isArray(photos)) return [];
  return photos.filter((item) => typeof item === "string" && item.trim().length > 0);
}

export function dedupePhotoUrls(photos) {
  const seen = new Set();
  return filterValidPhotos(photos).filter((url) => {
    if (seen.has(url)) return false;
    seen.add(url);
    return true;
  });
}

async function preparePhotosForSave(photos) {
  const uniquePhotos = dedupePhotoUrls(photos);
  const prepared = [];
  const allowDataUrlFallback = isPreviewAuthEnabled();

  for (const source of uniquePhotos) {
    if (typeof source === "string" && source.startsWith("data:")) {
      try {
        const uploaded = await uploadProfileImage(source);
        prepared.push({
          url: uploaded.url,
          storageKey: uploaded.storageKey,
        });
      } catch (error) {
        if (allowDataUrlFallback) {
          prepared.push({ url: source });
          continue;
        }
        throw error;
      }
      continue;
    }

    prepared.push({ url: source, storageKey: null });
  }

  return prepared;
}

export function clearStaleLocalProfileArtifacts() {
  removeItem(storageKeys.onboardingDraft);
  removeItem(storageKeys.profilePhotos);
  clearOnboardingResumePath();
}

export async function syncOwnProfileFromServer(userId = "me") {
  if (isPreviewAuthEnabled()) {
    return readCachedProfile(userId);
  }

  try {
    return await fetchOwnProfile(userId);
  } catch {
    return readCachedProfile(userId);
  }
}

export function normalizeStoredProfile(stored, userId = "me") {
  const photos = dedupePhotoUrls(stored.photos);
  const image = stored.image || photos[0] || "";
  const username = stored.username ? String(stored.username).trim() : "";
  const name = (stored.name || stored.displayName || username || "").trim();
  const age = stored.age != null && stored.age !== "" ? Number(stored.age) : null;
  const profileTypes = normalizeProfileTypes(
    Array.isArray(stored.profileTypes)
      ? stored.profileTypes
      : stored.category
        ? [stored.category]
        : [],
  );

  return {
    id: stored.id ?? userId,
    userId: stored.userId ?? userId,
    name,
    username,
    age,
    title: stored.title || (username && age ? `@${username}, ${age}` : username ? `@${username}` : name),
    displayName: username || name,
    desc: stored.desc || stored.bio || "",
    bio: stored.bio || stored.desc || "",
    country: stored.country || "",
    city: stored.city || "",
    latitude: stored.latitude ?? stored.lat ?? null,
    longitude: stored.longitude ?? stored.lng ?? null,
    profileTypes,
    category: profileTypes[0] ?? "",
    languages: normalizeLanguageEntries(stored.languages),
    services: Array.isArray(stored.services) ? stored.services : [],
    serviceFor: Array.isArray(stored.serviceFor) ? stored.serviceFor : [],
    photos: photos.length ? photos : image ? [image] : [],
    image,
    photoCount: stored.photoCount ?? photos.length,
    contact: {
      whatsapp: "",
      phone: "",
      webcamEnabled: false,
      ...(stored.contact || {}),
    },
    verified: resolveIdVerified(stored),
    premium: Boolean(stored.premium) || (stored.services || []).includes("Premium"),
    isVisible: stored.isVisible ?? true,
    onboardingComplete: Boolean(stored.onboardingComplete),
    locationSource: stored.locationSource || null,
    settings: stored.settings || {},
  };
}

function resolveIdVerified(stored) {
  if (stored.idVerified === true) return true;
  if (stored.verified !== true) return false;
  return !(stored.services || []).includes("Verified");
}

const EMPTY_OWN_PROFILE = {
  id: "me",
  userId: "me",
  name: "",
  username: "",
  age: null,
  title: "",
  displayName: "",
  desc: "",
  bio: "",
  country: "",
  city: "",
  latitude: null,
  longitude: null,
  profileTypes: [],
  category: "",
  languages: [],
  services: [],
  serviceFor: [],
  photos: [],
  image: "",
  photoCount: 0,
  contact: { whatsapp: "", phone: "", webcamEnabled: false },
  verified: false,
  premium: false,
  isVisible: true,
  onboardingComplete: false,
  locationSource: null,
  settings: {},
};

export const PROFILE_UPDATED_EVENT = "greydates:profile-updated";

export function notifyProfileUpdated() {
  window.dispatchEvent(new CustomEvent(PROFILE_UPDATED_EVENT));
}

export function cacheProfileLocally(profile) {
  if (!profile) return;
  const photos = dedupePhotoUrls(profile.photos);
  saveItem(storageKeys.profile, {
    ...profile,
    photos: [],
    image: profile.image || photos[0] || "",
    photoCount: photos.length,
  });
  if (photos.length) {
    saveItem(storageKeys.profilePhotos, photos);
  } else {
    removeItem(storageKeys.profilePhotos);
  }
}

function isEmptyCachedProfile(stored) {
  if (!stored) return true;

  const username = String(stored.username || "").trim();
  const hasPhotos = (stored.photoCount > 0)
    || (Array.isArray(stored.photos) && stored.photos.some(Boolean));
  const hasBio = String(stored.bio || stored.desc || "").trim();
  const hasLocation = String(stored.city || "").trim();
  const hasAge = stored.age != null && stored.age !== "";

  return !username && !hasPhotos && !hasBio && !hasLocation && !hasAge;
}

function readLocalOnboardingDraftProfile(userId = "me") {
  const draft = loadItem(storageKeys.onboardingDraft, DEFAULT_ONBOARDING_DRAFT);
  const normalizedDraft = normalizeOnboardingDraft(draft);
  const storedPhotos = filterValidPhotos(loadItem(storageKeys.profilePhotos));
  const photos = storedPhotos.length ? storedPhotos : normalizedDraft.photos;

  if (!normalizedDraft.username && !photos.length && !normalizedDraft.city && normalizedDraft.age === "") {
    return null;
  }

  return draftToProfile({
    ...normalizedDraft,
    photos,
    photoSlots: normalizedDraft.photoSlots,
  }, userId);
}

function readCachedProfile(userId = "me") {
  try {
    const onboardingDone = Boolean(loadItem(storageKeys.onboardingComplete));
    const stored = loadItem(storageKeys.profile);
    const draftProfile = onboardingDone ? null : readLocalOnboardingDraftProfile(userId);

    if (stored && !isEmptyCachedProfile(stored)) {
      const photos = dedupePhotoUrls(loadItem(storageKeys.profilePhotos));
      const normalized = normalizeStoredProfile({
        ...stored,
        photos: photos.length ? photos : stored.photos,
        image: photos[0] || stored.image || "",
        photoCount: photos.length || stored.photoCount || 0,
      }, userId);

      if (draftProfile) {
        return normalizeStoredProfile({
          ...normalized,
          username: normalized.username || draftProfile.username,
          name: normalized.name || draftProfile.name,
          age: normalized.age ?? draftProfile.age,
          country: normalized.country || draftProfile.country,
          city: normalized.city || draftProfile.city,
          profileTypes: normalized.profileTypes.length ? normalized.profileTypes : draftProfile.profileTypes,
          bio: normalized.bio || draftProfile.bio,
          desc: normalized.desc || draftProfile.desc,
          languages: normalized.languages.length ? normalized.languages : draftProfile.languages,
          services: normalized.services.length ? normalized.services : draftProfile.services,
          serviceFor: normalized.serviceFor.length ? normalized.serviceFor : draftProfile.serviceFor,
          photos: normalized.photos.length ? normalized.photos : draftProfile.photos,
          image: normalized.image || draftProfile.image,
          contact: {
            ...draftProfile.contact,
            ...normalized.contact,
          },
        }, userId);
      }

      return normalized;
    }

    if (draftProfile) {
      return draftProfile;
    }
  } catch (error) {
    console.warn("Failed to load cached profile", error);
  }

  return {
    ...EMPTY_OWN_PROFILE,
    id: userId,
    userId,
  };
}

export function loadOwnProfile(userId = "me") {
  return readCachedProfile(userId);
}

export async function fetchOwnProfile(userId = "me") {
  if (isPreviewAuthEnabled()) {
    return readCachedProfile(userId);
  }

  try {
    const profile = await fetchMyProfile();
    if (!profile) {
      return readCachedProfile(userId);
    }
    const normalized = normalizeStoredProfile(profile, userId);
    cacheProfileLocally(normalized);
    return normalized;
  } catch (error) {
    console.warn("Failed to fetch profile from API", error);
    return readCachedProfile(userId);
  }
}

export async function saveOwnPhotos(photos, userId = "me") {
  const uniquePhotos = dedupePhotoUrls(photos);

  if (isPreviewAuthEnabled()) {
    if (uniquePhotos.length) {
      saveItem(storageKeys.profilePhotos, uniquePhotos);
    } else {
      removeItem(storageKeys.profilePhotos);
    }
    notifyProfileUpdated();
    return { ok: true };
  }

  try {
    const prepared = await preparePhotosForSave(uniquePhotos);
    await replaceMyPhotos(prepared);

    const saved = await fetchMyProfile();
    if (saved) {
      const normalized = normalizeStoredProfile(saved, userId);
      cacheProfileLocally(normalized);
      notifyProfileUpdated();
      return { ok: true, profile: normalized };
    }

    notifyProfileUpdated();
    return { ok: true };
  } catch (error) {
    const message = error?.response?.data?.message
      || "Could not save your photos. Please try again.";
    return { ok: false, error: message };
  }
}

export async function saveOwnProfile(profile, { onboardingComplete = true } = {}) {
  if (isPreviewAuthEnabled()) {
    return saveOwnProfileLocally(profile);
  }

  try {
    const photos = dedupePhotoUrls(profile.photos);
    const { photos: _photos, photoCount: _photoCount, image: _image, ...profileMeta } = profile;

    await upsertMyProfile(profileMeta, { onboardingComplete });

    const prepared = await preparePhotosForSave(photos);
    await replaceMyPhotos(prepared);

    const saved = await fetchMyProfile();
    if (!saved) {
      return { ok: false, error: "Could not load your saved profile." };
    }

    const normalized = normalizeStoredProfile(saved, profile.id || profile.userId);
    cacheProfileLocally(normalized);
    notifyProfileUpdated();
    return { ok: true, profile: normalized };
  } catch (error) {
    const message = error?.response?.data?.message
      || "Could not save your profile. Please try again.";
    return { ok: false, error: message };
  }
}

function saveOwnProfileLocally(profile) {
  const photos = dedupePhotoUrls(profile.photos);
  const image = profile.image || photos[0] || "";

  if (photos.length) {
    if (!saveItem(storageKeys.profilePhotos, photos)) {
      return {
        ok: false,
        error: "Could not save your profile photos. Try using fewer or smaller photos.",
      };
    }
  } else {
    removeItem(storageKeys.profilePhotos);
  }

  if (!saveItem(storageKeys.profile, {
    ...profile,
    photos: [],
    image,
    photoCount: photos.length,
  })) {
    return { ok: false, error: "Could not save your profile. Your browser storage may be full." };
  }

  notifyProfileUpdated();
  return { ok: true, profile: normalizeStoredProfile({ ...profile, photos, image }, profile.id) };
}

export function getProfileDisplayName(profile) {
  if (!profile) return "Your profile";

  const username = normalizeUsername(profile.username || "");
  if (username) {
    const handle = `@${username}`;
    if (profile.age != null && profile.age !== "") {
      return `${handle}, ${profile.age}`;
    }
    return handle;
  }

  const name = (profile.name || profile.displayName || "").trim();
  if (!name) return "Your profile";
  if (profile.age != null && profile.age !== "") {
    return `${name}, ${profile.age}`;
  }
  return name;
}

export { getProfileLocationLabel } from "../../shared/utils/profileLocation";

export function getProfileContactSummary(profile) {
  const contact = profile?.contact || {};
  const whatsapp = contact.whatsapp ? String(contact.whatsapp).trim() : "";
  const phone = contact.phone ? String(contact.phone).trim() : "";

  return {
    whatsapp,
    phone,
    hasWhatsapp: whatsapp.length > 0,
    hasPhone: phone.length > 0,
    hasWebcam: Boolean(contact.webcamEnabled),
  };
}
